import { addSeconds, max } from 'date-fns';
import { ChatQueryStatus, ChatResponseType } from '@trinity/chat';
import type { ChatState, QueriesState } from './interfaces';

function handleQuerySubmitted(
  state: QueriesState.State,
  action: Extract<ChatState.Action, { type: 'query-submitted' }>
): QueriesState.State {
  state[action.payload.identifier] = {
    attachments: action.payload.attachments || [],
    createdOn: getSafeCreatedOn(state),
    id: null,
    identifier: action.payload.identifier,
    value: action.payload.value,
    rating: null,
    response: null,
    state: null,
    status: ChatQueryStatus.Loading,
    citations: [],
    mapping: {
      parentIdentifier: action.payload.parentQueryIdentifier,
      childrenIdentifiers: [],
    },
    suggestedFollowups: [],
    hideUserMessage: action.payload.hideUserMessage,
    metadata: {},
    agentProgress: null,
    inputAttachments: action.payload.inputAttachments,
  };

  const erroredIdentifiers: string[] = [];

  if (action.payload.parentQueryIdentifier) {
    const parent = state[action.payload.parentQueryIdentifier];
    if (parent) {
      const parentChildrenIdentifiers = parent.mapping?.childrenIdentifiers ?? [];
      state[action.payload.parentQueryIdentifier] = {
        ...parent,
        mapping: {
          parentIdentifier: parent.mapping?.parentIdentifier ?? null,
          childrenIdentifiers: [...parentChildrenIdentifiers, action.payload.identifier].filter(
            (identifier) => !erroredIdentifiers.includes(identifier)
          ),
        },
      };
    }
  }

  return Object.assign({}, state);
}

function handleQueryVariantSubmitted(
  state: QueriesState.State,
  action: Extract<ChatState.Action, { type: 'query-variant-submitted' }>
): QueriesState.State {
  const source = state[action.payload.sourceIdentifier];
  if (!source) {
    return Object.assign({}, state);
  }

  const sourceMappingParentIdentifier = source.mapping?.parentIdentifier ?? null;

  state[action.payload.identifier] = {
    attachments: action.payload.attachments || [],
    createdOn: getSafeCreatedOn(state),
    id: null,
    identifier: action.payload.identifier,
    value: source.value,
    rating: null,
    response: null,
    state: {},
    status: ChatQueryStatus.Loading,
    citations: [],
    mapping: {
      parentIdentifier: sourceMappingParentIdentifier,
      childrenIdentifiers: [],
    },
    suggestedFollowups: [],
    hideUserMessage: action.payload.hideUserMessage,
    metadata: {},
    agentProgress: null,
  };

  const erroredIdentifiers: string[] = [];

  if (sourceMappingParentIdentifier) {
    const sourceParent = state[sourceMappingParentIdentifier];
    if (sourceParent) {
      const sourceParentChildrenIdentifiers = sourceParent.mapping?.childrenIdentifiers ?? [];
      state[sourceMappingParentIdentifier] = {
        ...sourceParent,
        mapping: {
          parentIdentifier: sourceParent.mapping?.parentIdentifier ?? null,
          childrenIdentifiers: [...sourceParentChildrenIdentifiers, action.payload.identifier].filter(
            (identifier) => !erroredIdentifiers.includes(identifier)
          ),
        },
      };
    }
  }

  return Object.assign({}, state);
}

function getSafeCreatedOn(state: QueriesState.State) {
  return max([new Date(), ...Object.values(state).map((q) => addSeconds(q.createdOn, 1))]);
}

export function queriesReducer(state: QueriesState.State, action: ChatState.Action): QueriesState.State {
  switch (action.type) {
    case 'query-submitted':
      return handleQuerySubmitted(state, action);

    case 'query-variant-submitted':
      return handleQueryVariantSubmitted(state, action);

    case 'query-response-available':
    case 'query-variant-response-available':
      return handleQueryResponseAvailable(state, action);

    case 'query-response-streaming':
      return handleQueryStreaming(state, action);

    case 'query-errored':
    case 'query-variant-errored': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        status: ChatQueryStatus.Error,
        sentryTraceId: action.payload.traceId,
        langfuseTraceUrl: action.payload.langfuseTraceUrl,
      };
      return Object.assign({}, state);
    }

    case 'query-rated': {
      state[action.payload.queryIdentifier] = Object.assign({}, state[action.payload.queryIdentifier], {
        rating: action.payload.rating,
      });
      return Object.assign({}, state);
    }

    case 'query-taking-long': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        status: ChatQueryStatus.LoadingExtended,
      };
      return Object.assign({}, state);
    }

    case 'query-followups-available': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        suggestedFollowups: action.payload.items,
        status: ChatQueryStatus.FollowupsAvailable,
      };
      return Object.assign({}, state);
    }

    case 'query-updated': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        ...action.payload.query,
      };
      return Object.assign({}, state);
    }

    case 'query-cancelled': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        status: ChatQueryStatus.Cancelled,
      };
      return Object.assign({}, state);
    }

    default:
      return state;
  }
}

function handleQueryResponseAvailable(
  state: QueriesState.State,
  action: Extract<ChatState.Action, { type: 'query-response-available' | 'query-variant-response-available' }>
): QueriesState.State {
  state[action.payload.query.identifier] = {
    ...state[action.payload.query.identifier],
    createdOn: action.payload.query.createdOn,
    attachments: action.payload.query.attachments || [],
    citations: action.payload.query.citations,
    id: action.payload.query.id,
    response: action.payload.query.response,
    mapping: action.payload.query.mapping,
    status: ChatQueryStatus.Complete,
    suggestedFollowups: action.payload.query.suggestedFollowups,
    langfuseTraceUrl: action.payload.query.langfuseTraceUrl,
    state: action.payload.query.state,
    metadata: action.payload.query.metadata,
    agentProgress: action.payload.query.agentProgress ?? null,
  };
  return Object.assign({}, state);
}

function handleQueryStreaming(
  state: QueriesState.State,
  action: Extract<ChatState.Action, { type: 'query-response-streaming' }>
): QueriesState.State {
  if (!state[action.payload.queryIdentifier]) {
    console.warn(`Query with identifier ${action.payload.queryIdentifier} not found for streaming update.`);
    return state;
  }
  state[action.payload.queryIdentifier] = {
    ...state[action.payload.queryIdentifier],
    status: ChatQueryStatus.Streaming,
    response: {
      ...state[action.payload.queryIdentifier].response,
      type: ChatResponseType.MarkdownHtml,
      value: (state[action.payload.queryIdentifier].response?.value || '') + action.payload.textChunk,
    },
  };
  return Object.assign({}, state);
}
